import { useCallback, useState } from "react";
import { Platform } from "react-native";
import { useStytch } from "./useStytch";

// Hidden test-only password sign-in flow for the nightly visual-test harness.
// Only activates when the Stytch project is a TEST project AND ?test=1 was
// requested. Any other combination leaves the caller's UI unchanged.
//
// The ?test=1 flag is "sticky" for the browser session: multi-step flows like
// onboarding navigate between routes and drop the query string, which would
// otherwise hide the test form on later screens (e.g. CreateAccountScreen).
// We latch it in sessionStorage once seen so it survives in-session
// navigation. Still hard-gated by the TEST-project token prefix, so this
// never activates against a live project.
function isTestModeRequested(): boolean {
  if (Platform.OS !== "web" || typeof window === "undefined") return false;
  try {
    if (new URLSearchParams(window.location.search).get("test") === "1") {
      window.sessionStorage?.setItem("louise_test_mode", "1");
      return true;
    }
    return window.sessionStorage?.getItem("louise_test_mode") === "1";
  } catch {
    return false;
  }
}

export function useTestSignIn({ onSuccess }: { onSuccess: () => void }) {
  const stytch = useStytch();

  const stytchToken = process.env.EXPO_PUBLIC_STYTCH_PUBLIC_TOKEN ?? "";
  const isTestEnv = stytchToken.startsWith("public-token-test-");
  const visible = isTestEnv && isTestModeRequested();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = useCallback(async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await stytch.passwords.authenticate({
        email,
        password,
        session_duration_minutes: 60,
      });
      onSuccess();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to sign in. Try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [stytch, email, password, onSuccess]);

  // Companion to `submit`: creates a brand-new test user via the Stytch
  // password flow so QA can spin up a fresh per-persona account without a
  // pre-seeded login. Same TEST-project + ?test=1 gating as sign-in.
  const submitSignUp = useCallback(async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await stytch.passwords.create({
        email,
        password,
        session_duration_minutes: 60,
      });
      onSuccess();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to create account. Try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [stytch, email, password, onSuccess]);

  return {
    visible,
    email,
    setEmail,
    password,
    setPassword,
    error,
    isSubmitting,
    submit,
    submitSignUp,
  };
}
